import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Newspaper, Trash2, Edit, Plus, Image as ImageIcon, Video, FileText, ExternalLink, Eye, EyeOff, Share2, Facebook, Twitter, Linkedin, MessageCircle } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const AdminNewsPage = () => {
  const [news, setNews] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    news_id: '',
    title: '',
    description: '',
    type: 'article',
    media_url: '',
    article_content: '',
    external_link: '',
    is_active: true
  });

  useEffect(() => { loadNews(); }, []);

  const loadNews = async () => {
    try {
      const response = await axios.get(`${API}/news?active_only=false`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      setNews(response.data);
    } catch (error) {
      toast.error('Failed to load news');
    }
  };

  const resetForm = () => {
    setFormData({
      news_id: '',
      title: '',
      description: '',
      type: 'article',
      media_url: '',
      article_content: '',
      external_link: '',
      is_active: true
    });
    setEditMode(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is 10MB. Your file: ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem("session_token")}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });
      
      setFormData(prev => ({ ...prev, media_url: response.data.url }));
      toast.success('Media uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 413) {
        toast.error('File too large for server. Try compressing the image.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload media');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        media_url: formData.media_url || null,
        article_content: formData.article_content || null,
        external_link: formData.external_link || null,
        is_active: formData.is_active
      };

      if (editMode) {
        await axios.put(`${API}/news/${formData.news_id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        toast.success('News updated successfully!');
      } else {
        await axios.post(`${API}/news`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        toast.success('News created successfully!');
      }
      
      setDialogOpen(false);
      resetForm();
      loadNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save news');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      news_id: item.news_id,
      title: item.title || '',
      description: item.description || '',
      type: item.type || 'article',
      media_url: item.media_url || '',
      article_content: item.article_content || '',
      external_link: item.external_link || '',
      is_active: item.is_active !== false
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (newsId) => {
    if (!confirm('Are you sure you want to delete this news item?')) return;
    
    try {
      await axios.delete(`${API}/news/${newsId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      toast.success('News deleted successfully!');
      loadNews();
    } catch (error) {
      toast.error('Failed to delete news');
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API}/news/${item.news_id}`, { is_active: !item.is_active }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      toast.success(`News ${item.is_active ? 'hidden' : 'published'}!`);
      loadNews();
    } catch (error) {
      toast.error('Failed to update news');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo': return <ImageIcon className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const handleShare = (platform, item) => {
    const baseUrl = window.location.origin;
    const shareUrl = item.external_link || `${baseUrl}/news/${item.news_id}`;
    const shareText = encodeURIComponent(`${item.title} - Lions Club Jaipur Hawamahal`);
    const shareUrlEncoded = encodeURIComponent(shareUrl);
    
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrlEncoded}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlEncoded}`;
        break;
      case 'whatsapp':
        url = `https://api.whatsapp.com/send?text=${shareText}%20${shareUrlEncoded}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
        return;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>In News</h2>
          <p className="text-sm text-muted-foreground">Manage news items displayed on the landing page</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
        >
          <Plus className="w-4 h-4 mr-2" /> Add News
        </Button>
      </div>

      <div className="grid gap-4">
        {news.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Newspaper className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No news items yet. Add your first news!</p>
            </CardContent>
          </Card>
        ) : (
          news.map((item) => (
            <Card key={item.news_id} className={`${!item.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {item.media_url && (
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      {item.type === 'video' ? (
                        <video src={getImageUrl(item.media_url)} className="w-full h-full object-cover" />
                      ) : (
                        <img src={getImageUrl(item.media_url)} alt={item.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" 
                        style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                        {getTypeIcon(item.type)}
                        {item.type}
                      </span>
                      {!item.is_active && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Hidden</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg truncate" style={{ color: '#0F172A' }}>{item.title}</h3>
                    {item.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                    )}
                    {item.external_link && (
                      <a href={item.external_link} target="_blank" rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: '#D4AF37' }}>
                        <ExternalLink className="w-3 h-3" /> External Link
                      </a>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Created: {formatDate(item.created_at)}
                      {item.created_by_name && ` by ${item.created_by_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" title="Share to social media">
                          <Share2 className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleShare('facebook', item)}>
                          <Facebook className="w-4 h-4 mr-2 text-blue-600" />
                          Share on Facebook
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShare('twitter', item)}>
                          <Twitter className="w-4 h-4 mr-2 text-sky-500" />
                          Share on Twitter/X
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShare('linkedin', item)}>
                          <Linkedin className="w-4 h-4 mr-2 text-blue-700" />
                          Share on LinkedIn
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShare('whatsapp', item)}>
                          <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                          Share on WhatsApp
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShare('copy', item)}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Copy Link
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="ghost" size="icon" onClick={() => toggleActive(item)}>
                      {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.news_id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit News' : 'Add News'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="News title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="article">Article</SelectItem>
                  <SelectItem value="photo">Photo</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
                rows={2}
              />
            </div>

            {formData.type === 'article' && (
              <div className="space-y-2">
                <Label>Article Content</Label>
                <Textarea
                  value={formData.article_content}
                  onChange={(e) => setFormData({ ...formData, article_content: e.target.value })}
                  placeholder="Full article content..."
                  rows={6}
                />
              </div>
            )}

            {(formData.type === 'photo' || formData.type === 'video') && (
              <div className="space-y-2">
                <Label>Upload {formData.type === 'photo' ? 'Photo' : 'Video'}</Label>
                <Input
                  type="file"
                  accept={formData.type === 'photo' ? 'image/*' : 'video/*'}
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  disabled={uploading}
                />
                {formData.media_url && (
                  <div className="mt-2">
                    {formData.type === 'video' ? (
                      <video src={getImageUrl(formData.media_url)} className="w-full max-h-48 object-contain rounded" controls />
                    ) : (
                      <img src={getImageUrl(formData.media_url)} alt="Preview" className="w-full max-h-48 object-contain rounded" />
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>External Link (optional)</Label>
              <Input
                type="url"
                value={formData.external_link}
                onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                placeholder="https://example.com/news-article"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_active">Publish (visible on landing page)</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={uploading}>
                {uploading ? 'Uploading...' : (editMode ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNewsPage;
